/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { createContext, useContext } from "react"
import { alpha, theme } from "../../styles/theme/theme"

// ─── Context for propagating disabled/loading from Bar to children ────────────

export const ViewControlsContext = createContext<{ disabled: boolean; loading: boolean }>({
  disabled: false,
  loading: false,
})

/** Returns true if the control should be disabled (own prop OR parent context). */
export function useControlDisabled(ownDisabled?: boolean): boolean {
  const ctx = useContext(ViewControlsContext)
  return ownDisabled || ctx.disabled || ctx.loading
}

// ─── Shared styles ───────────────────────────────────────────────────────────

export const chipRow = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  flex-wrap: wrap;
  flex: 1;
`

export const flexRow = css`
  display: flex;
  align-items: center;
`

export const chip = css`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: ${theme.typography.fontSize.xs};
  background: ${alpha(theme.colors.foreground, 0.08)};
  color: ${alpha(theme.colors.foreground, 0.87)};
  white-space: nowrap;
  border: none;
`

export const chipDelete = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  margin: 0 -3px 0 2px;
  cursor: pointer;
  color: ${alpha(theme.colors.foreground, 0.26)};
  border-radius: 50%;
  &:hover { color: ${alpha(theme.colors.foreground, 0.4)}; }
`

export const dropArrow = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;
  &::after {
    content: "";
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid ${alpha(theme.colors.foreground, 0.54)};
  }
`

export const bareSelectTrigger = css`
  height: auto;
  min-height: 20px;
  width: auto;
  border: none;
  border-radius: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  box-shadow: none;
  gap: 0;
  &[data-size="default"], &[data-size="sm"] {
    height: auto;
    min-height: 20px;
  }
  &:focus { box-shadow: none; outline: none; }
  & > svg { display: none; }
`

export const dropdownContentProps = { align: "start" as const, sideOffset: 4 }
